import React, { useEffect, useState } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { billingQueue as urls } from '@URLS'
import { toast } from '@CommonComponents/toast'
import { LoadingSpinner, METable } from '@CommonComponents'
import { Pagination } from '@CommonComponents/buttons'
import { CreateInvoiceAccountModal } from './CreateInvoiceAccountModal'

const PAGE_SIZE = 20

const InvoiceAccountCard = ({ account, onNavigate }) => {
  const [, dispatch] = useStateValue()
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(account.name)
  const [billingContact, setBillingContact] = useState(account.billing_contact || '')
  const [notes, setNotes] = useState(account.notes || '')
  const [saving, setSaving] = useState(false)

  const handleCancel = () => {
    setName(account.name)
    setBillingContact(account.billing_contact || '')
    setNotes(account.notes || '')
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Account name is required.')
      return
    }
    setSaving(true)
    try {
      const resp = await client.post(urls.updateInvoiceAccount(), {
        invoice_account_id: account.id,
        name: name.trim(),
        billing_contact: billingContact.trim(),
        notes: notes.trim(),
      })
      dispatch({ type: 'UPDATE_INVOICE_ACCOUNT', payload: resp.data })
      setIsEditing(false)
      toast.success('Invoice account updated.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update invoice account.')
    }
    setSaving(false)
  }

  return (
    <div className={`card-myed mb-3 ${isEditing ? 'border-primary' : ''}`}>
      <div className="card-body">
        {isEditing ? (
          <div>
            <div className="form-group">
              <label className="small font-weight-bold">Name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={name}
                onChange={e => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="small font-weight-bold">Billing Contact</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={billingContact}
                onChange={e => setBillingContact(e.target.value)}
              />
            </div>
            <div className="form-group mb-2">
              <label className="small font-weight-bold">Notes</label>
              <textarea
                className="form-control form-control-sm"
                rows="2"
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
              <button
                className="btn btn-sm btn-myed btn-default btn-default-myed"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-myed btn-primary btn-primary-myed"
                onClick={handleSave}
                disabled={saving}
              >
                {saving && <i className="fa-regular fa-spinner fa-spin mr-1" />}
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <h6 className="mb-1">
                <a href="#" onClick={e => { e.preventDefault(); onNavigate() }}>{account.name}</a>
              </h6>
              {account.billing_contact && (
                <p className="small text-muted mb-1">
                  <i className="fa-regular fa-envelope mr-1" />
                  {account.billing_contact}
                </p>
              )}
              {account.notes && (
                <p className="small text-muted mb-1" style={{ fontStyle: 'italic' }}>
                  {account.notes}
                </p>
              )}
              <p className="small text-muted mb-0">
                {account.course_count || 0} course{account.course_count !== 1 ? 's' : ''} assigned
              </p>
            </div>
            <button
              className="btn btn-sm btn-myed btn-default btn-default-myed ml-2"
              onClick={() => setIsEditing(true)}
            >
              <i className="fa-regular fa-edit mr-1" />
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const SchoolDetail = ({ schoolId, onBack, history }) => {
  const [state, dispatch] = useStateValue()
  const { schoolDetail } = state
  const BASE = '/account/billing_queue'
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    const fetchDetail = async () => {
      dispatch({ type: 'SET_SCHOOL_DETAIL', payload: null })
      setLoading(true)
      try {
        const resp = await client.get(urls.getSchoolDetail(schoolId))
        dispatch({ type: 'SET_SCHOOL_DETAIL', payload: resp.data })
      } catch {
        dispatch({ type: 'SET_SCHOOL_DETAIL', payload: null })
        toast.error('Failed to load school details.')
      }
      setLoading(false)
    }
    fetchDetail()
  }, [schoolId])

  if (loading) {
    return (
      <div className="text-center py-5">
        <LoadingSpinner />
      </div>
    )
  }

  if (!schoolDetail) return null

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1" style={{ background: 'none', padding: 0 }}>
              <li className="breadcrumb-item">
                <a href="#" onClick={e => { e.preventDefault(); onBack() }}>
                  Schools
                </a>
              </li>
              <li className="breadcrumb-item active">{schoolDetail.name}</li>
            </ol>
          </nav>
          <h4 className="mb-0">{schoolDetail.name}</h4>
        </div>
        <button
          className="btn btn-sm btn-myed btn-default-alt btn-default-alt-myed"
          onClick={() => setModalOpen(true)}
        >
          <i className="fa-regular fa-plus mr-1" />
          New Invoice Account
        </button>
      </div>

      {schoolDetail.invoice_accounts.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fa-regular fa-file-invoice fa-2x text-muted mb-3" />
            <h5 className="text-muted">No invoice accounts</h5>
            <p className="text-muted small mb-0">
              Add an invoice account to track billing for this school.
            </p>
          </div>
        </div>
      ) : (
        schoolDetail.invoice_accounts.map(account => (
          <InvoiceAccountCard
            key={account.id}
            account={account}
            onNavigate={() => history.push(`${BASE}/schools/${schoolDetail.id}/accounts/${account.id}`)}
          />
        ))
      )}

      <CreateInvoiceAccountModal
        show={modalOpen}
        toggle={() => setModalOpen(false)}
        defaultSchoolId={schoolDetail.id}
      />
    </div>
  )
}

const InvoiceAccountDetail = ({ accountId, schoolId, history }) => {
  const BASE = '/account/billing_queue'
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true)
      try {
        const resp = await client.get(urls.getInvoiceAccountDetail(accountId))
        setDetail(resp.data)
      } catch {
        toast.error('Failed to load invoice account details.')
      }
      setLoading(false)
    }
    fetchDetail()
  }, [accountId])

  if (loading) {
    return (
      <div className="text-center py-5">
        <LoadingSpinner />
      </div>
    )
  }

  if (!detail) return null

  const numPages = Math.ceil(detail.courses.length / PAGE_SIZE)
  const paged = detail.courses.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const tableData = paged.map(br => [
    <span key={br.id}>
      <div>{br.course.title}</div>
      <small className="text-muted">#{br.course.coursenum}</small>
    </span>,
    br.invoice_type === 'one_time'
      ? <span>One Time{br.billing_date ? <span className="text-muted ml-1 small">({br.billing_date})</span> : null}</span>
      : <span>Recurring{br.billing_frequency ? <span className="text-muted ml-1 small">({br.billing_frequency === 'custom' ? br.billing_frequency_custom : br.billing_frequency})</span> : null}</span>,
    br.billing_price ? `$${br.billing_price}` : '—',
    br.invoice_number || '—',
    br.invoice_type === 'one_time'
      ? <span className={`label label-${br.payment_status === 'paid' ? 'success' : 'default'}`}>{br.payment_status === 'paid' ? 'Paid' : 'Unpaid'}</span>
      : '—',
  ])

  return (
    <div>
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-1" style={{ background: 'none', padding: 0 }}>
          <li className="breadcrumb-item">
            <a href="#" onClick={e => { e.preventDefault(); history.push(BASE + '/schools') }}>Schools</a>
          </li>
          <li className="breadcrumb-item">
            <a href="#" onClick={e => { e.preventDefault(); history.push(`${BASE}/schools/${schoolId}`) }}>
              {detail.school ? detail.school.name : 'School'}
            </a>
          </li>
          <li className="breadcrumb-item active">{detail.name}</li>
        </ol>
      </nav>
      <h4 className="mb-1">{detail.name}</h4>
      {detail.billing_contact && (
        <p className="text-muted small mb-1">
          <i className="fa-regular fa-envelope mr-1" />
          {detail.billing_contact}
        </p>
      )}
      {detail.notes && (
        <p className="text-muted small mb-3">{detail.notes}</p>
      )}

      <div className="card-myed mt-3">
        {detail.courses.length === 0 ? (
          <div className="text-center py-5">
            <i className="fa-regular fa-file-invoice fa-2x text-muted mb-3" />
            <h5 className="text-muted">No courses assigned</h5>
            <p className="text-muted small mb-0">
              Courses assigned to this invoice account will appear here.
            </p>
          </div>
        ) : (
          <>
            <METable
              columns={['Course', 'Type', 'Amount', 'Invoice #', 'Payment']}
              data={tableData}
            />
            {numPages > 1 && (
              <Pagination
                currentPage={currentPage}
                rate={PAGE_SIZE}
                total={detail.courses.length}
                setCurrentPage={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export const SchoolsTab = ({ match, history }) => {
  const [state, dispatch] = useStateValue()
  const { schools } = state
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const schoolId = match && match.params && match.params.schoolId
  const accountId = match && match.params && match.params.accountId
  const BASE = '/account/billing_queue'

  useEffect(() => {
    if (schools.length > 0) return
    const fetchSchools = async () => {
      setLoading(true)
      try {
        const resp = await client.get(urls.getSchools())
        dispatch({ type: 'SET_SCHOOLS', payload: resp.data.schools })
      } catch {
        toast.error('Failed to load schools.')
      }
      setLoading(false)
    }
    fetchSchools()
  }, [])

  const handleSearchChange = value => {
    setSearch(value)
    setCurrentPage(1)
  }

  if (schoolId && accountId) {
    return (
      <InvoiceAccountDetail
        accountId={accountId}
        schoolId={schoolId}
        history={history}
      />
    )
  }

  if (schoolId) {
    return (
      <SchoolDetail
        schoolId={schoolId}
        history={history}
        onBack={() => history.push(BASE + '/schools')}
      />
    )
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <LoadingSpinner />
      </div>
    )
  }

  const filtered = search.trim()
    ? schools.filter(s => s.name.toLowerCase().includes(search.trim().toLowerCase()))
    : schools

  const numPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const tableData = paged.map(school => [
    <a
      key={school.id}
      href="#"
      onClick={e => { e.preventDefault(); history.push(`${BASE}/schools/${school.id}`) }}
    >
      {school.name}
    </a>,
    school.invoice_account_count,
  ])

  return (
    <div className="card-myed">
      <h4 className="mb-3">Schools</h4>

      <div className="input-group input-group-sm mb-3" style={{ maxWidth: '300px' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Filter schools..."
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
        />
        {search && (
          <div className="input-group-append">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => handleSearchChange('')}
            >
              <i className="fa-regular fa-times" />
            </button>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-5">
          <i className="fa-regular fa-university fa-2x text-muted mb-3" />
          <h5 className="text-muted">
            {search ? `No schools match "${search}"` : 'No schools found'}
          </h5>
          {!search && (
            <p className="text-muted small mb-0">
              Schools will appear here once they are connected to courses.
            </p>
          )}
        </div>
      ) : (
        <>
          <METable
            columns={['School', 'Invoice Accounts']}
            data={tableData}
          />
          {numPages > 1 && (
            <Pagination
              currentPage={currentPage}
              rate={PAGE_SIZE}
              total={filtered.length}
              setCurrentPage={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  )
}
